import React, { Component } from 'react'
import ReactDOM from 'react-dom'

class SharedModal extends Component {
  constructor(props) {
    super(props)
    this.rootEl = null
    this.portalRoot = null
    this.portalElement = null
    this.originalParent = null
    this.originalNextSibling = null
  }

  overlayStyle() {
    const isOpen = !!this.props.isOpen

    return {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: isOpen ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      overflowX: 'hidden',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      padding: 'calc(1rem + env(safe-area-inset-top, 0px)) 1rem calc(1rem + env(safe-area-inset-bottom, 0px))',
      boxSizing: 'border-box',
      pointerEvents: isOpen ? 'auto' : 'none',
      zIndex: 9999,
    }
  }

  panelStyle() {
    return {
      position: 'relative',
      flexShrink: 0,
      width: '100%',
      margin: 'auto',
    }
  }

  portalToBody() {
    if (this.props.useBodyPortal || !this.rootEl) {
      return
    }

    if (this.portalRoot && this.rootEl.parentNode === this.portalRoot) {
      return
    }

    this.originalParent = this.rootEl.parentNode
    this.originalNextSibling = this.rootEl.nextSibling

    this.portalRoot = document.createElement('div')
    this.portalRoot.setAttribute('data-shared-modal-root', this.props.id || '')
    this.portalRoot.style.position = 'fixed'
    this.portalRoot.style.top = '0'
    this.portalRoot.style.right = '0'
    this.portalRoot.style.bottom = '0'
    this.portalRoot.style.left = '0'
    this.portalRoot.style.zIndex = '9999'
    this.portalRoot.style.pointerEvents = 'none'

    document.documentElement.appendChild(this.portalRoot)
    this.portalRoot.appendChild(this.rootEl)
  }

  restoreFromBody() {
    if (this.props.useBodyPortal || !this.rootEl || !this.originalParent) {
      return
    }

    if (this.originalNextSibling && this.originalNextSibling.parentNode === this.originalParent) {
      this.originalParent.insertBefore(this.rootEl, this.originalNextSibling)
    } else {
      this.originalParent.appendChild(this.rootEl)
    }

    if (this.portalRoot && this.portalRoot.parentNode) {
      this.portalRoot.parentNode.removeChild(this.portalRoot)
    }
    this.portalRoot = null
  }

  toggleScrollLock(locked) {
    document.body.style.overflow = locked ? 'hidden' : ''
    document.documentElement.style.overflow = locked ? 'hidden' : ''

    const yieldContainer = document.getElementById('yield-container')
    if (yieldContainer) {
      yieldContainer.style.overflow = locked ? 'hidden' : ''
      if (locked) yieldContainer.scrollTop = 0
    }

    if (locked) {
      window.scrollTo(0, 0)
    }
  }

  syncParentContainer(isOpen) {
    const { parentContainer } = this.props
    if (!parentContainer) {
      return
    }

    const parentContainerEl = document.querySelector(`#${parentContainer} .bg-container`)
    if (!parentContainerEl) {
      return
    }

    if (isOpen) {
      parentContainerEl.classList.add('bg-transparent')
      parentContainerEl.classList.remove('bg-white')
    } else {
      parentContainerEl.classList.remove('bg-transparent')
      parentContainerEl.classList.add('bg-white')
    }
  }

  componentDidMount() {
    if (this.props.useBodyPortal) {
      this.portalElement = document.createElement('div')
      document.body.appendChild(this.portalElement)
      this.syncPortal()
    } else {
      this.portalToBody()
    }

    if (this.props.isOpen) {
      this.syncParentContainer(true)
      this.toggleScrollLock(true)
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props

    if (prevProps.isOpen !== isOpen) {
      this.syncParentContainer(isOpen)
      this.toggleScrollLock(isOpen)
    }

    if (this.props.useBodyPortal && (
      prevProps.isOpen !== this.props.isOpen ||
      prevProps.id !== this.props.id ||
      prevProps.title !== this.props.title ||
      prevProps.maxWidth !== this.props.maxWidth
    )) {
      this.syncPortal()
    }
  }

  componentWillUnmount() {
    if (this.portalElement) {
      ReactDOM.unmountComponentAtNode(this.portalElement)

      if (this.portalElement.parentNode) {
        this.portalElement.parentNode.removeChild(this.portalElement)
      }
    }

    this.toggleScrollLock(false)
    this.syncParentContainer(false)
    this.restoreFromBody()
  }

  handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && this.props.onClose) {
      this.props.onClose()
    }
  }

  syncPortal() {
    if (!this.portalElement) {
      return
    }

    if (!this.props.isOpen) {
      ReactDOM.unmountComponentAtNode(this.portalElement)
      return
    }

    ReactDOM.render(this.renderModal(), this.portalElement)
  }

  renderModal() {
    const {
      id,
      bgColor,
      children,
      maxWidth,
      isCustom,
      onClose,
      headerPadding,
      innerPadding,
      title,
      surfaceClassName,
      wrapperClassName,
      titleClassName,
      closeIconClass,
    } = this.props

    if (isCustom) {
      return (
        <div
          id={id}
          role='dialog'
          aria-modal='true'
          tabIndex='-1'
          className='shared-modal fixed inset-0 z-[9999] flex w-full items-center justify-center overflow-x-hidden overflow-y-auto bg-gray-900/50 p-4'
        >
          {children}
        </div>
      )
    }

    return (
      <div
        id={id}
        role='dialog'
        aria-modal='true'
        tabIndex='-1'
        className='shared-modal fixed inset-0 z-[9999] flex w-full items-center justify-center overflow-x-hidden overflow-y-auto bg-gray-900/50 p-4'
        onClick={this.handleBackdropClick}
      >
        <div
          className={['relative z-10 w-full max-h-full shrink-0 pointer-events-auto', maxWidth || 'max-w-2xl', wrapperClassName].filter(Boolean).join(' ')}
        >
          <div
            className={[
              'relative bg-container pointer-events-auto',
              bgColor || 'bg-white',
              surfaceClassName || 'rounded-lg md:shadow',
              innerPadding || 'p-3 md:p-7',
            ].join(' ')}
          >
            { onClose && (
              <div className={`flex items-center justify-between border-b border-base-border ${headerPadding || 'px-4 py-3'}`}>
                <h3 className={titleClassName || 'text-lg font-medium text-base-gray-text-main'}>
                  {title || ''}
                </h3>
                <button
                  type='button'
                  className='text-base-gray-text-secondary hover:text-base-gray-text-main focus:outline-none'
                  onClick={onClose}
                >
                  <hero-icon-solid name='x' class={closeIconClass || 'h-5 w-5'}></hero-icon-solid>
                </button>
              </div>
            )}
            {children}
          </div>
        </div>
      </div>
    )
  }

  render() {
    if (this.props.useBodyPortal) {
      return null
    }

    const {
      id,
      bgColor,
      children,
      maxWidth,
      isCustom,
      onClose,
      headerPadding,
      innerPadding,
      title,
      surfaceClassName,
      wrapperClassName,
      titleClassName,
      closeIconClass,
    } = this.props

    if (isCustom) {
      return (
        <div
          ref={(el) => { this.rootEl = el }}
          id={id}
          data-modal-backdrop='static'
          tabIndex='-1'
          aria-hidden={this.props.isOpen ? 'false' : 'true'}
          style={this.overlayStyle()}
        >
          {children}
        </div>
      )
    }

    return (
      <div
        ref={(el) => { this.rootEl = el }}
        id={id}
        data-modal-backdrop='static'
        tabIndex='-1'
        aria-hidden={this.props.isOpen ? 'false' : 'true'}
        style={this.overlayStyle()}
        onClick={this.handleBackdropClick}
      >
        <div
          className={[maxWidth || 'max-w-2xl', wrapperClassName].filter(Boolean).join(' ')}
          style={this.panelStyle()}
        >
          <div
            className={[
              'relative bg-container',
              bgColor || 'bg-white',
              surfaceClassName || 'rounded-lg md:shadow',
              innerPadding || 'p-3 md:p-7',
            ].join(' ')}
          >
            { onClose && (
              <div className={`flex items-center justify-between border-b border-base-border ${headerPadding || 'px-4 py-3'}`}>
                <h3 className={titleClassName || 'text-lg font-medium text-base-gray-text-main'}>
                  {title || ''}
                </h3>
                <button
                  type='button'
                  className='text-base-gray-text-secondary hover:text-base-gray-text-main focus:outline-none'
                  onClick={onClose}
                >
                  <hero-icon-solid name='x' class={closeIconClass || 'h-5 w-5'}></hero-icon-solid>
                </button>
              </div>
            )}
            {children}
          </div>
        </div>
      </div>
    )
  }
}

export default SharedModal
